import React, { useEffect, useState, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import api from '../services/api';

// ── Helpers ────────────────────────────────────────────────────────────────

const fmt = (n: any) => Number(n || 0).toFixed(2);

const CONFIANZA_BADGE: Record<string, { label: string; color: string }> = {
  alto:  { label: 'Auto-detectado ✓', color: '#22c55e' },
  medio: { label: 'Parcial',           color: '#f59e0b' },
  bajo:  { label: 'Manual',            color: '#ef4444' },
};

const ESTADO_COLOR: Record<string, string> = {
  pendiente:  '#f59e0b',
  confirmado: '#22c55e',
  rechazado:  '#64748b',
};

interface Toast {
  id: number;
  monto: number;
  nombrePagador: string;
  confianza: string;
}

// ── Component ──────────────────────────────────────────────────────────────

const YapeNotificaciones: React.FC = () => {
  const [notificaciones, setNotificaciones] = useState<any[]>([]);
  const [stats, setStats]   = useState({ pendientes: 0, confirmados: 0, rechazados: 0, totalMonto: 0 });
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro]   = useState('');

  // Confirm modal state
  const [modal, setModal]               = useState<any | null>(null);
  const [clientes, setClientes]         = useState<any[]>([]);
  const [facturas, setFacturas]         = useState<any[]>([]);
  const [clienteSel, setClienteSel]     = useState('');
  const [facturaSel, setFacturaSel]     = useState('');
  const [cargandoFact, setCargandoFact] = useState(false);
  const [guardando, setGuardando]       = useState(false);

  // Toast notifications
  const [toasts, setToasts] = useState<Toast[]>([]);
  const socketRef = useRef<Socket | null>(null);

  // ── Data loading ──────────────────────────────────────────────────────────

  const cargarStats = useCallback(async () => {
    try {
      const r = await api.get('/yape/notificaciones/stats');
      setStats(r.data);
    } catch { /* silenciar */ }
  }, []);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filtro) params.estado = filtro;
      const [nRes] = await Promise.all([
        api.get('/yape/notificaciones', { params }),
        cargarStats(),
      ]);
      setNotificaciones(nRes.data);
    } catch { /* silenciar */ }
    finally { setLoading(false); }
  }, [filtro, cargarStats]);

  useEffect(() => { cargar(); }, [cargar]);

  // ── WebSocket ─────────────────────────────────────────────────────────────

  useEffect(() => {
    const base = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || '';
    const socket = io(`${base}/yape`, { transports: ['websocket'] });
    socketRef.current = socket;

    socket.on('nueva_yape', (notif: any) => {
      const toast: Toast = {
        id: Date.now(),
        monto: Number(notif.monto || 0),
        nombrePagador: notif.nombrePagador || 'Desconocido',
        confianza: notif.confianza || 'bajo',
      };
      setToasts(prev => [...prev, toast]);
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== toast.id)), 8000);
      cargar();
    });

    socket.on('yape_confirmada', () => { cargar(); });

    return () => { socket.disconnect(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Confirm modal helpers ─────────────────────────────────────────────────

  const abrirModal = async (notif: any) => {
    setModal(notif);
    setClienteSel(notif.clienteId || '');
    setFacturaSel(notif.facturaId || '');
    setFacturas([]);
    try {
      const r = await api.get('/clientes');
      const lista = Array.isArray(r.data) ? r.data : (r.data?.data || []);
      setClientes(lista);
    } catch { setClientes([]); }

    if (notif.clienteId) cargarFacturas(notif.clienteId, notif.facturaId);
  };

  const cargarFacturas = async (clienteId: string, preselect?: string) => {
    setCargandoFact(true);
    try {
      const r = await api.get(`/facturas/cliente/${clienteId}`);
      const todas: any[] = Array.isArray(r.data) ? r.data : [];
      const pendientes = todas.filter((f: any) => f.estado === 'pendiente');
      setFacturas(pendientes);
      if (preselect && pendientes.find((f: any) => f.id === preselect)) {
        setFacturaSel(preselect);
      }
    } catch { setFacturas([]); }
    finally { setCargandoFact(false); }
  };

  const onClienteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    setClienteSel(id);
    setFacturaSel('');
    setFacturas([]);
    if (id) cargarFacturas(id);
  };

  const confirmar = async () => {
    if (!clienteSel) { alert('Selecciona un cliente'); return; }
    setGuardando(true);
    try {
      await api.put(`/yape/notificaciones/${modal.id}/confirmar`, {
        clienteId: clienteSel,
        facturaId: facturaSel || undefined,
      });
      setModal(null);
      cargar();
    } catch { alert('Error al confirmar'); }
    finally { setGuardando(false); }
  };

  const rechazar = async () => {
    if (!window.confirm('¿Rechazar este pago Yape?')) return;
    setGuardando(true);
    try {
      await api.put(`/yape/notificaciones/${modal.id}/rechazar`);
      setModal(null);
      cargar();
    } catch { alert('Error al rechazar'); }
    finally { setGuardando(false); }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const badge = (conf: string) => CONFIANZA_BADGE[conf] ?? CONFIANZA_BADGE.bajo;

  return (
    <div>
      {/* ── Toast notifications ── */}
      <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {toasts.map(t => (
          <div key={t.id} style={{
            background: t.confianza === 'alto' ? '#14532d' : '#1e293b',
            border: `1px solid ${t.confianza === 'alto' ? '#22c55e' : '#f59e0b'}`,
            borderRadius: 10, padding: '12px 18px', color: '#f1f5f9',
            boxShadow: '0 4px 20px #00000066', minWidth: 280, maxWidth: 360,
          }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>
              🟡 Nuevo pago Yape: S/{fmt(t.monto)}
            </div>
            <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 3 }}>
              De: {t.nombrePagador}
            </div>
            <div style={{ marginTop: 8 }}>
              <button
                onClick={() => { setToasts(p => p.filter(x => x.id !== t.id)); cargar(); }}
                style={{ background: 'transparent', border: '1px solid #475569', color: '#94a3b8', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}
              >
                Ver detalles
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ color: '#f1f5f9', margin: 0, fontSize: 24, fontWeight: 800 }}>💳 Pagos Yape</h2>
          <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: 13 }}>
            Notificaciones de pago Yape con auto-detección de clientes
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select
            value={filtro}
            onChange={e => setFiltro(e.target.value)}
            style={{ background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0', borderRadius: 8, padding: '8px 12px', fontSize: 13 }}
          >
            <option value="">Todos</option>
            <option value="pendiente">Pendientes</option>
            <option value="confirmado">Confirmados</option>
            <option value="rechazado">Rechazados</option>
          </select>
          <button
            onClick={cargar}
            style={{ background: '#0ea5e9', border: 'none', color: 'white', padding: '9px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
          >
            ↻ Actualizar
          </button>
        </div>
      </div>

      {/* ── Stats row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Pendientes',  value: stats.pendientes,  color: '#f59e0b' },
          { label: 'Confirmados', value: stats.confirmados, color: '#22c55e' },
          { label: 'Rechazados',  value: stats.rechazados,  color: '#ef4444' },
          { label: 'Total S/.', value: `S/${fmt(stats.totalMonto)}`, color: '#38bdf8' },
        ].map(s => (
          <div key={s.label} style={{ background: '#1e293b', borderRadius: 10, padding: '14px 18px', border: `1px solid ${s.color}33` }}>
            <div style={{ color: '#64748b', fontSize: 12 }}>{s.label}</div>
            <div style={{ color: s.color, fontSize: 24, fontWeight: 800 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div style={{ color: '#64748b', textAlign: 'center', padding: 48 }}>Cargando...</div>
      ) : notificaciones.length === 0 ? (
        <div style={{ color: '#64748b', textAlign: 'center', padding: 48 }}>No hay notificaciones</div>
      ) : (
        <div style={{ background: '#1e293b', borderRadius: 12, overflow: 'hidden', border: '1px solid #334155' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #334155' }}>
                {['Fecha', 'Pagador', 'Monto', 'Código', 'Confianza', 'Cliente', 'Estado', 'Acciones'].map(h => (
                  <th key={h} style={{ padding: '12px 14px', textAlign: 'left', color: '#64748b', fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {notificaciones.map((n, i) => {
                const conf = badge(n.confianza);
                const estadoColor = ESTADO_COLOR[n.estado] ?? '#64748b';
                return (
                  <tr key={n.id} style={{ borderBottom: '1px solid #0f172a', background: i % 2 === 0 ? '#0f172a' : '#1e293b' }}>
                    <td style={{ padding: '11px 14px', color: '#64748b', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {n.createdAt ? new Date(n.createdAt).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                    <td style={{ padding: '11px 14px', color: '#e2e8f0', fontSize: 14, fontWeight: 600 }}>
                      {n.nombrePagador || <span style={{ color: '#475569' }}>—</span>}
                    </td>
                    <td style={{ padding: '11px 14px', color: '#22c55e', fontSize: 15, fontWeight: 700 }}>
                      S/{fmt(n.monto)}
                    </td>
                    <td style={{ padding: '11px 14px', color: '#94a3b8', fontSize: 12, fontFamily: 'monospace' }}>
                      {n.codigoOperacion || '—'}
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{ background: conf.color + '22', color: conf.color, borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>
                        {conf.label}
                      </span>
                    </td>
                    <td style={{ padding: '11px 14px', color: '#94a3b8', fontSize: 13 }}>
                      {n.cliente
                        ? `${n.cliente.nombres} ${n.cliente.apellidos}`
                        : <span style={{ color: '#475569' }}>Sin asignar</span>}
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{ background: estadoColor + '22', color: estadoColor, borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
                        {n.estado}
                      </span>
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      {n.estado === 'pendiente' && (
                        <button
                          onClick={() => abrirModal(n)}
                          style={{ background: '#22c55e22', border: '1px solid #22c55e44', color: '#22c55e', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                        >
                          Confirmar
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Confirm modal ── */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: '#00000099', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#1e293b', borderRadius: 14, padding: 28, width: 520, border: '1px solid #334155', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ color: '#f1f5f9', margin: '0 0 20px', fontSize: 18 }}>Confirmar pago Yape</h3>

            {/* SMS raw */}
            <label style={{ color: '#64748b', fontSize: 12, display: 'block', marginBottom: 6 }}>SMS ORIGINAL</label>
            <div style={{ background: '#0f172a', borderRadius: 8, padding: '10px 14px', color: '#94a3b8', fontSize: 13, marginBottom: 16, fontFamily: 'monospace', lineHeight: 1.5 }}>
              {modal.smsRaw}
            </div>

            {/* Parsed info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
              <div style={{ background: '#0f172a', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                <div style={{ color: '#64748b', fontSize: 11 }}>Monto</div>
                <div style={{ color: '#22c55e', fontSize: 20, fontWeight: 800 }}>S/{fmt(modal.monto)}</div>
              </div>
              <div style={{ background: '#0f172a', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                <div style={{ color: '#64748b', fontSize: 11 }}>Pagador</div>
                <div style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 600 }}>{modal.nombrePagador || '—'}</div>
              </div>
              <div style={{ background: '#0f172a', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                <div style={{ color: '#64748b', fontSize: 11 }}>Código</div>
                <div style={{ color: '#94a3b8', fontSize: 13, fontFamily: 'monospace' }}>{modal.codigoOperacion || '—'}</div>
              </div>
            </div>

            {/* Confianza banner */}
            {modal.confianza === 'alto' && (
              <div style={{ background: '#14532d', border: '1px solid #22c55e44', borderRadius: 8, padding: '8px 14px', color: '#22c55e', fontSize: 13, marginBottom: 16 }}>
                ✓ Cliente y factura detectados automáticamente
              </div>
            )}

            {/* Cliente selector */}
            <label style={{ color: '#94a3b8', fontSize: 13, display: 'block', marginBottom: 4 }}>Cliente *</label>
            <select
              value={clienteSel}
              onChange={onClienteChange}
              style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 14, boxSizing: 'border-box' }}
            >
              <option value="">-- Seleccionar cliente --</option>
              {clientes.map((c: any) => (
                <option key={c.id} value={c.id}>{c.nombres} {c.apellidos} {c.codigo ? `(${c.codigo})` : ''}</option>
              ))}
            </select>

            {/* Factura selector */}
            <label style={{ color: '#94a3b8', fontSize: 13, display: 'block', marginBottom: 4 }}>
              Factura {cargandoFact ? '(cargando...)' : '(opcional)'}
            </label>
            <select
              value={facturaSel}
              onChange={e => setFacturaSel(e.target.value)}
              disabled={!clienteSel || cargandoFact}
              style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0', borderRadius: 8, padding: '8px 12px', marginBottom: 22, fontSize: 14, boxSizing: 'border-box', opacity: (!clienteSel || cargandoFact) ? 0.5 : 1 }}
            >
              <option value="">-- Sin asignar factura --</option>
              {facturas.map((f: any) => (
                <option key={f.id} value={f.id}>
                  {f.numero || f.id.slice(0, 8)} — S/{fmt(f.monto)} — {f.descripcion || `${f.mes}/${f.anio}`}
                </option>
              ))}
            </select>
            {clienteSel && !cargandoFact && facturas.length === 0 && (
              <p style={{ color: '#f59e0b', fontSize: 12, marginTop: -18, marginBottom: 14 }}>
                Este cliente no tiene facturas pendientes.
              </p>
            )}

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setModal(null)}
                disabled={guardando}
                style={{ background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: 14 }}
              >
                Cancelar
              </button>
              <button
                onClick={rechazar}
                disabled={guardando}
                style={{ background: 'transparent', border: '1px solid #ef444466', color: '#ef4444', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: 14 }}
              >
                Rechazar
              </button>
              <button
                onClick={confirmar}
                disabled={guardando || !clienteSel}
                style={{ background: guardando || !clienteSel ? '#166534' : '#22c55e', border: 'none', color: 'white', borderRadius: 8, padding: '8px 22px', cursor: 'pointer', fontWeight: 700, fontSize: 14, opacity: guardando || !clienteSel ? 0.6 : 1 }}
              >
                {guardando ? 'Guardando...' : 'Confirmar pago'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default YapeNotificaciones;
